import React, { useRef } from 'react';
import { FieldSetProps } from './FieldSet.types';
import { st, classes } from './FieldSet.st.css';
import {
  ALIGNMENT,
  DIRECTION,
  GAP,
  LEGEND_ALIGNMENT,
  LEGEND_PLACEMENT,
  LEGEND_SIZE,
  dataHooks,
} from './constants';

import { Label } from './components/Label';
import { Suffix } from './components/Suffix';
import { StatusMessage } from './components/StatusMessage';
import { InlineLabelWrapper } from './components/InlineLabelWrapper';
import { FieldSetContext } from './FieldSet.context';
import _uniqueId from 'lodash/uniqueId';

const FieldSet = ({
  dataHook,
  children,
  legend: label,
  legendPlacement: labelPlacement = LEGEND_PLACEMENT.TOP,
  legendAlignment: labelAlignment = LEGEND_ALIGNMENT.MIDDLE,
  legendSize: labelSize = LEGEND_SIZE.SMALL,
  required = false,
  infoContent,
  infoTooltipProps,
  suffix,
  charCount,
  status,
  statusMessage,
  statusId,
  alignment = ALIGNMENT.START,
  direction = DIRECTION.HORIZONTAL,
  gap = GAP.SMALL,
  columns,
  ellipsis,
  maxLines = 1,
}: FieldSetProps) => {
  const hasSuffix: boolean = !!(suffix || charCount !== undefined);
  const hasOnlySuffix = labelPlacement === LEGEND_PLACEMENT.NONE && hasSuffix;
  const hasInlineElements =
    labelPlacement === LEGEND_PLACEMENT.LEFT ||
    labelPlacement === LEGEND_PLACEMENT.RIGHT;

  const SuffixWithProps = hasSuffix ? (
    <Suffix
      suffix={suffix}
      charCount={charCount}
      labelPlacement={labelPlacement}
      hasInlineElements={hasInlineElements}
      labelSize={labelSize}
    />
  ) : null;

  // TODO: useId() hook, when React@18
  const { current: uniqueStatusMessageId } = useRef(
    _uniqueId('fieldset-status-message-'),
  );

  return (
    <fieldset
      data-hook={dataHook}
      data-legend-placement={labelPlacement}
      data-legend-alignment={labelAlignment}
      data-status={status}
      data-alignment={alignment}
      data-direction={direction}
      data-gap={gap}
      className={st(classes.root, {
        labelSize,
        hasInlineElements,
        labelPlacement,
        labelAlignment,
        required,
        hasOnlySuffix,
        minLabelHeight: !children,
      })}
    >
      <legend className={classes.visuallyHidden}>{label}</legend>
      {labelPlacement === LEGEND_PLACEMENT.TOP && (
        <div className={classes.labelRow}>
          <div className={classes.labelRowMain}>
            <Label
              dataHook={dataHooks.label}
              label={label}
              labelSize={labelSize}
              ellipsis={ellipsis ?? true}
              maxLines={maxLines}
              required={required}
              infoContent={infoContent}
              infoTooltipProps={infoTooltipProps}
              aria-hidden
            />
          </div>
          {SuffixWithProps}
        </div>
      )}
      {hasOnlySuffix && (
        <div className={st(classes.suffixRow)}>{SuffixWithProps}</div>
      )}

      {children && (
        <FieldSetContext.Provider
          value={statusId ? {} : { status, statusId: uniqueStatusMessageId }}
        >
          <div
            data-hook={dataHooks.children}
            className={st(classes.children, {
              childrenWithInlineLabel: hasInlineElements,
              alignment,
              direction,
              gap,
            })}
            style={{
              gridTemplateColumns:
                direction === DIRECTION.HORIZONTAL ? columns : undefined,
            }}
          >
            {children}
          </div>
        </FieldSetContext.Provider>
      )}

      {hasInlineElements && (
        <InlineLabelWrapper
          suffix={suffix}
          charCount={charCount}
          labelPlacement={labelPlacement}
          labelSize={labelSize}
        >
          <Label
            dataHook={dataHooks.label}
            label={label}
            labelSize={labelSize}
            ellipsis={ellipsis}
            maxLines={maxLines}
            required={required}
            infoContent={infoContent}
            infoTooltipProps={infoTooltipProps}
            aria-hidden
          />
          {SuffixWithProps}
        </InlineLabelWrapper>
      )}

      {statusMessage && (
        <StatusMessage
          statusId={statusId || uniqueStatusMessageId}
          status={status}
          statusMessage={statusMessage}
          labelSize="tiny"
          labelPlacement={labelPlacement}
        />
      )}
    </fieldset>
  );
};

FieldSet.displayName = 'FieldSet';

export { FieldSet };
